// src/components/common/GlobalSettingsControl.tsx
// Header ⚙ control + SettingsDrawer on routes that don't use LeadWorkspaceHeader (welcome, gates, admin).
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useAppTheme } from "@/contexts/ThemeContext";
import { SettingsDrawer } from "@/components/common/SettingsDrawer";
import type { OrgLocation } from "@/config/locations";
import type { Location } from "@/types/location";
import {
  getVoiceEnabled,
  getVoiceProvider,
  getSpeakMode,
  setVoiceEnabled as persistVoiceEnabled,
  setVoiceProvider as persistVoiceProvider,
  setSpeakMode as persistSpeakMode,
  subscribeVoiceSettings,
} from "@/lib/speech/voiceSettings";

export type GlobalSettingsControlHandle = {
  /** Opens the settings drawer (e.g. prompt incomplete profile). */
  open: () => void;
};

export type GlobalSettingsControlProps = {
  className?: string;
  /** Extra classes on the gear button (layout host often sets spacing). */
  buttonClassName?: string;
  /** `icon` = transparent header glyph; `chip` = bordered pill (welcome / gates). */
  variant?: "icon" | "chip";
  showLeadsControls?: boolean;
  emptyLeadAutoDeleteEnabled?: boolean;
  setEmptyLeadAutoDeleteEnabled?: (v: boolean) => void;
  syncedCount?: number;
  viableCount?: number;
  isSyncing?: boolean;
  onSyncAllLeads?: () => Promise<void>;
  location?: Location | null;
  selectableLocations?: OrgLocation[];
  setLocation?: (l: Location) => void;
  locationsLoaded?: boolean;
  attentionPulse?: boolean;
  /** Amber pulse on the gear when KB gap inbox has open items (trainer+). */
  kbGapAttention?: boolean;
};

export const GlobalSettingsControl = forwardRef<GlobalSettingsControlHandle, GlobalSettingsControlProps>(
  function GlobalSettingsControl(
    {
      className = "",
      buttonClassName = "",
      variant = "chip",
      showLeadsControls = false,
      emptyLeadAutoDeleteEnabled = true,
      setEmptyLeadAutoDeleteEnabled = () => {},
      syncedCount = 0,
      viableCount = 0,
      isSyncing = false,
      onSyncAllLeads = async () => {},
      location = null,
      selectableLocations = [],
      setLocation = () => {},
      locationsLoaded = false,
      attentionPulse = false,
      kbGapAttention = false,
    },
    ref
  ) {
    const { theme, setTheme } = useAppTheme();
    const [open, setOpen] = useState(false);
    const [voiceEnabled, setVoiceEnabled] = useState(getVoiceEnabled);
    const [voiceProvider, setVoiceProvider] = useState(getVoiceProvider);
    const [speakMode, setSpeakMode] = useState(getSpeakMode);

    useImperativeHandle(ref, () => ({
      open: () => setOpen(true),
    }));

    useEffect(() => {
      return subscribeVoiceSettings((snap) => {
        setVoiceEnabled(snap.enabled);
        setVoiceProvider(snap.provider);
        setSpeakMode(snap.speakMode);
      });
    }, []);

    const chipBtn =
      "rounded-full border border-slate-200/90 dark:border-slate-600 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm px-2.5 py-1.5 text-lg leading-none text-slate-600 dark:text-slate-300 shadow-sm hover:bg-white dark:hover:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-500";
    const iconBtn =
      "border-0 bg-transparent shadow-none px-1 text-lg leading-none text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-md hover:bg-gray-100/60 dark:hover:bg-white/10";
    const pulseBtn =
      "animate-pulse text-blue-600 dark:text-slate-100 " +
      "shadow-[0_0_14px_rgba(59,130,246,0.55)] dark:shadow-[0_0_18px_rgba(226,232,240,0.42)] " +
      "ring-2 ring-blue-400/55 dark:ring-slate-300/45";
    const amberPulseBtn =
      "animate-pulse text-amber-800 dark:text-amber-100 " +
      "shadow-[0_0_16px_rgba(245,158,11,0.55)] dark:shadow-[0_0_18px_rgba(251,191,36,0.35)] " +
      "ring-2 ring-amber-400/70 dark:ring-amber-500/50";

    const baseBtn = variant === "icon" ? iconBtn : chipBtn;
    const attentionClass = kbGapAttention ? amberPulseBtn : attentionPulse ? pulseBtn : "";

    return (
      <>
        <div className={`flex items-center ${className}`.trim()}>
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Open settings"
            className={`${baseBtn} ${attentionClass} ${buttonClassName}`.trim()}
            title="Settings — appearance, voice, and more"
          >
            ⚙
          </button>
        </div>
        <SettingsDrawer
          open={open}
          onClose={() => setOpen(false)}
          theme={theme}
          setTheme={setTheme}
          voiceEnabled={voiceEnabled}
          setVoiceEnabled={(v) => {
            persistVoiceEnabled(v);
            setVoiceEnabled(v);
          }}
          voiceProvider={voiceProvider}
          setVoiceProvider={(p) => {
            persistVoiceProvider(p);
            setVoiceProvider(p);
          }}
          speakMode={speakMode}
          setSpeakMode={(m) => {
            persistSpeakMode(m);
            setSpeakMode(m);
          }}
          showLeadsControls={showLeadsControls}
          emptyLeadAutoDeleteEnabled={emptyLeadAutoDeleteEnabled}
          setEmptyLeadAutoDeleteEnabled={setEmptyLeadAutoDeleteEnabled}
          syncedCount={syncedCount}
          viableCount={viableCount}
          isSyncing={isSyncing}
          onSyncAllLeads={onSyncAllLeads}
          location={location}
          selectableLocations={selectableLocations}
          setLocation={setLocation}
          locationsLoaded={locationsLoaded}
        />
      </>
    );
  }
);
